import React, { useEffect, useMemo, useRef, useState } from "react";
import getMonthName from "../utils/getMonthName";
import getDaysOfWeek from "../utils/getDaysOfWeek";
import getCalendarDates from "../utils/getCalendarDates";
import { DateRangePickerProps } from "../components/date-range-picker/DateRangePicker";
import useHandleMonthChange from "./useHandleMonthChange";

function useDateRangeLogic({
  disabledDates,
  weekStartsOn,
  onRangeChange,
  customDaysOfWeek,
  visibleMonths = 1,
  preSelectedRange,
}: DateRangePickerProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isMonthDropdownVisible, setIsMonthDropdownVisible] = useState(false);
  const [selectedRange, setSelectedRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>(
    preSelectedRange ?? {
      start: null,
      end: null,
    }
  );
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [yearInput, setYearInput] = useState(String(currentDate.getFullYear()));

  const daysOfWeek = getDaysOfWeek(weekStartsOn, customDaysOfWeek);

  const months = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  const isDateDisabled = (date: Date) => {
    return disabledDates?.some(
      (disabledDate) => date.toDateString() === disabledDate.toDateString()
    );
  };

  const isStartDate = (date: Date) => {
    return (
      selectedRange.start &&
      selectedRange.start.toDateString() === date.toDateString()
    );
  };

  const isEndDate = (date: Date) => {
    return (
      selectedRange.end &&
      selectedRange.end.toDateString() === date.toDateString()
    );
  };

  const isInRange = (date: Date) => {
    const { start, end } = selectedRange;
    if (start && end) {
      return date > start && date < end;
    } else if (start && hoveredDate) {
      return (
        (date > start && date < hoveredDate) ||
        (date < start && date > hoveredDate)
      );
    }
    return false;
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return;

    const { start, end } = selectedRange;

    if (!start || (start && end)) {
      setSelectedRange({ start: date, end: null });
    } else if (start && !end) {
      if (date < start) {
        setSelectedRange({ start: date, end: start });
        if (onRangeChange) {
          onRangeChange({ start: date, end: start });
        }
      } else {
        setSelectedRange({ start, end: date });
        if (onRangeChange) {
          onRangeChange({ start, end: date });
        }
      }
    }
  };

  const { handleMonthChange, handleNextMonth, handlePreviousMonth } =
    useHandleMonthChange({ setCurrentDate, visibleMonths });

  const monthsRange = useMemo(() => {
    return visibleMonths > 1
      ? `${getMonthName(months, currentDate.getMonth())} - ${getMonthName(
          months,
          currentDate.getMonth() + (visibleMonths - 1)
        )}`
      : getMonthName(months, currentDate.getMonth());
  }, [currentDate, months, visibleMonths]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setYearInput(String(currentDate.getFullYear()));
  }, [currentDate]);

  const handleClickOutside = (e: MouseEvent) => {
    if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
      setIsMonthDropdownVisible(false);
    }
  };

  useEffect(() => {
    if (isMonthDropdownVisible) {
      document.addEventListener("click", handleClickOutside);

      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }

    return undefined;
  }, [isMonthDropdownVisible]);

  const handleYearInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (/^\d{0,4}$/.test(value)) {
      setYearInput(value);
      if (value.length === 4) {
        setCurrentDate((prev) => new Date(Number(value), prev.getMonth(), 1));
      }
    }
  };

  return {
    handleYearInputChange,
    dropdownRef,
    inputRef,
    months,
    monthsRange,
    currentDate,
    handleMonthChange,
    handleNextMonth,
    handlePreviousMonth,
    handleDateClick,
    isInRange,
    isEndDate,
    isStartDate,
    isDateDisabled,
    setHoveredDate,
    isMonthDropdownVisible,
    setIsMonthDropdownVisible,
    yearInput,
    daysOfWeek,
    getCalendarDates: getCalendarDates,
  };
}

export default useDateRangeLogic;
